/**
 * Auth Service - Better Auth integration
 * Handles authentication, session management, and token operations
 */

import { api, getErrorMessage } from './api.service';

// Auth types
export interface User {
  id: string;
  email: string;
  name?: string;
  role: 'admin' | 'customer';
  createdAt?: string;
  updatedAt?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  user: User;
  session?: {
    token: string;
    expiresAt: string;
  };
  message?: string;
}

export interface SessionResponse {
  user: User;
  authenticated: boolean;
  expiresAt?: string;
}

export interface LogoutResponse {
  message: string;
  success: boolean;
}

/**
 * Authentication Service
 */
class AuthService {
  private readonly AUTH_ENDPOINTS = {
    LOGIN: '/api/v1/auth/login',
    LOGOUT: '/api/v1/auth/logout',
    SESSION: '/api/v1/auth/session',
    REFRESH: '/api/v1/auth/refresh',
  };

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    console.log('[AuthService] Login request', {
      endpoint: this.AUTH_ENDPOINTS.LOGIN,
      email: credentials.email
    });

    try {
      const response = await api.post<LoginResponse>(
        this.AUTH_ENDPOINTS.LOGIN,
        credentials
      );

      console.log('[AuthService] Login response received', {
        user: response.user,
        hasSession: !!response.session
      });

      // Store session info if needed
      if (response.session) {
        this.storeSessionInfo(response.session);
      }

      // Store user info in sessionStorage for quick access
      if (response.user) {
        this.storeUserInfo(response.user);
      }

      return response;
    } catch (error) {
      console.error('[AuthService] Login error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<LogoutResponse> {
    try {
      const response = await api.post<LogoutResponse>(this.AUTH_ENDPOINTS.LOGOUT);

      // Clear stored session info
      this.clearSessionInfo();

      return response;
    } catch (error) {
      // Even if logout fails on server, clear local session
      this.clearSessionInfo();
      throw this.handleAuthError(error);
    }
  }

  /**
   * Check current session status
   */
  async checkSession(): Promise<SessionResponse> {
    try {
      const response = await api.get<SessionResponse>(this.AUTH_ENDPOINTS.SESSION);

      // Update stored user info if authenticated
      if (response.authenticated && response.user) {
        this.storeUserInfo(response.user);
      } else if (response.authenticated === false) {
        // Only clear if server explicitly says not authenticated
        this.clearSessionInfo();
      }
      // Don't clear on network error - preserve existing data

      return response;
    } catch (error) {
      // Check if it's an authentication error vs network error
      const isAuthError = (error as any).response?.status === 401 ||
                          (error as any).response?.status === 403;

      if (isAuthError) {
        // Authentication failed - clear session
        this.clearSessionInfo();
      }
      // For network errors (no response), preserve existing session data
      throw this.handleAuthError(error);
    }
  }

  /**
   * Refresh session token
   */
  async refreshSession(): Promise<SessionResponse> {
    try {
      const response = await api.post<SessionResponse>(this.AUTH_ENDPOINTS.REFRESH);

      if (response.user) {
        this.storeUserInfo(response.user);
      }

      return response;
    } catch (error) {
      // Check if it's an authentication error vs network error
      const isAuthError = (error as any).response?.status === 401 ||
                          (error as any).response?.status === 403;

      if (isAuthError) {
        // Authentication failed - clear session
        this.clearSessionInfo();
      }
      // For network errors, preserve session data (retry logic in AuthProvider will handle)
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get stored user info
   */
  getStoredUser(): User | null {
    try {
      const userJson = sessionStorage.getItem('user');
      if (!userJson) return null;

      return JSON.parse(userJson) as User;
    } catch (error) {
      console.error('[Get Stored User Error]', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated (based on stored session)
   */
  isAuthenticated(): boolean {
    const user = this.getStoredUser();
    return user !== null;
  }

  /**
   * Store session info
   */
  private storeSessionInfo(session: { token: string; expiresAt: string }): void {
    try {
      sessionStorage.setItem('session', JSON.stringify(session));
    } catch (error) {
      console.error('[Store Session Error]', error);
    }
  }

  /**
   * Store user info
   */
  private storeUserInfo(user: User): void {
    try {
      sessionStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      console.error('[Store User Error]', error);
    }
  }

  /**
   * Clear all stored session info
   */
  private clearSessionInfo(): void {
    try {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('session');
    } catch (error) {
      console.error('[Clear Session Error]', error);
    }
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: unknown): Error {
    const message = getErrorMessage(error);

    // Create a user-friendly error message
    if (message.toLowerCase().includes('unauthorized') ||
        message.toLowerCase().includes('invalid credentials')) {
      return new Error('Invalid email or password');
    }

    if (message.toLowerCase().includes('network') ||
        message.toLowerCase().includes('connection')) {
      return new Error('Unable to connect to server. Please check your connection.');
    }

    if (message.toLowerCase().includes('csrf')) {
      return new Error('Security token expired. Please refresh and try again.');
    }

    return new Error(message);
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export class for testing
export default AuthService;
